/** @ticket https://bubo.vretta.com/vea/project-management/vretta-project-notes/eqao/authoring/-/work_items/1986 */

/**
 * A repair as a check or an audit names it. On a check, `autoFixes: null` means
 * "use the audit's repair" and stops a built-in per-check default from applying.
 */
export interface IRepairRef {
  slug?: string;
  caption?: { en?: string, fr?: string };
}

interface IRepairAudit {
  autoFixes?: IRepairRef | null;
  isCompleteFix?: boolean;
  checks?: { id?: string, autoFix?: boolean, autoFixes?: IRepairRef | null }[];
}

export const repairSlugOf = (repair: IRepairRef | null | undefined): string => `${repair?.slug ?? ''}`.trim();

export const hasOwnCheckRepair = (check: { autoFixes?: IRepairRef | null } | null | undefined): boolean => !!repairSlugOf(check?.autoFixes);

/** The repair a check's wrench runs: the check's own when it names one, else the audit's. */
export const checkRepairOf = (audit: IRepairAudit | null | undefined, check: { autoFixes?: IRepairRef | null } | null | undefined): IRepairRef | null => {
  if (hasOwnCheckRepair(check)) {
    return check.autoFixes;
  }
  return audit?.autoFixes ?? null;
};

export const checkRepairSlug = (audit: IRepairAudit | null | undefined, checkId: string): string => {
  const check = (audit?.checks ?? []).find(c => c?.id === checkId);
  return repairSlugOf(checkRepairOf(audit, check));
};

/** Only the audit's own repair can be the generic steps; a check's repair is always a named one. */
export const isGenericRepairRun = (audit: IRepairAudit | null | undefined, patchSlug: string): boolean =>
  !!audit?.isCompleteFix && !!audit.autoFixes && repairSlugOf(audit.autoFixes) === `${patchSlug ?? ''}`.trim();

export const isAuditRepairable = (audit: IRepairAudit | null | undefined): boolean =>
  !!audit?.autoFixes || (audit?.checks ?? []).some(hasOwnCheckRepair);

/** Every repair the audit can run, audit's first; each also keys its progress counter. */
export const auditRepairSlugs = (audit: IRepairAudit | null | undefined): string[] => {
  const slugs: string[] = [];
  const add = (slug: string) => { if (slug && !slugs.includes(slug)) { slugs.push(slug); } };
  add(repairSlugOf(audit?.autoFixes));
  (audit?.checks ?? []).forEach(check => add(repairSlugOf(check?.autoFixes)));
  return slugs;
};

export interface IRepairGroup {
  slug: string;
  repair: IRepairRef;
  checkIds: string[];
}

/** Checks grouped by the repair they run, in check order; checks without any repair are left out. */
export const groupChecksByRepair = (audit: IRepairAudit | null | undefined, checkIds: string[]): IRepairGroup[] => {
  const groups: IRepairGroup[] = [];
  (checkIds ?? []).forEach(checkId => {
    const check = (audit?.checks ?? []).find(c => c?.id === checkId);
    const repair = checkRepairOf(audit, check);
    if (!repair) { return; }
    const slug = repairSlugOf(repair);
    const group = groups.find(g => g.slug === slug);
    if (group) {
      group.checkIds.push(checkId);
    } else {
      groups.push({ slug, repair, checkIds: [checkId] });
    }
  });
  return groups;
};
